// Cave84 — Authentication helpers
// Owner: Google Sign-In (popup with redirect fallback for iOS PWA standalone)
// Viewers: email-link (passwordless) — UI is deferred to Phase 7 polish.

import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  type User as FirebaseUser
} from 'firebase/auth';
import { auth, googleProvider } from './firebase';

/**
 * iOS Safari in PWA standalone mode breaks `signInWithPopup` because the popup
 * cannot open in a new window. Detect that case and use redirect there.
 */
function shouldUseRedirect(): boolean {
  if (typeof window === 'undefined') return false;
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-expect-error iOS-only Safari flag, not in standard navigator types
    window.navigator.standalone === true;
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  return isStandalone && isIOS;
}

export async function signInWithGoogle(): Promise<FirebaseUser | null> {
  if (shouldUseRedirect()) {
    await signInWithRedirect(auth, googleProvider);
    return null; // result will arrive via getRedirectResult on next load
  }
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

/**
 * Call once on app boot. If we returned from a redirect-based sign-in,
 * Firebase will resolve the result here. Returns the user or null.
 */
export async function consumeRedirectResult(): Promise<FirebaseUser | null> {
  try {
    const result = await getRedirectResult(auth);
    return result?.user ?? null;
  } catch {
    return null;
  }
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(auth);
}
